/**
 * An on-device trace of the sound engine, enabled by putting `audiodbg=1` in
 * the URL (before the "#": https://dev.fiveout.net/?audiodbg=1#/themes).
 *
 * It exists because the audio bugs worth fixing happen on a phone and nowhere
 * else: the iPhone suspends, interrupts and hands back audio contexts in ways no
 * desktop browser reproduces, and "two rooms' music playing at once" after
 * "Use this set" could not be made to happen in Chrome at all. So the phone
 * reports on itself: a small panel in the corner showing what is alive right
 * now (contexts and their states, live ambience outputs, the venue) and the
 * last few things that happened, with times. A screenshot of it when something
 * sounds wrong says what the engine did.
 *
 * Read once at module load off the whole href, like netdbg=1 and perf=1,
 * because HashRouter puts the app's own query after the "#". Off, every call
 * here is a no-op.
 */
export const audioDiagEnabled = typeof location !== "undefined" && /[?&]audiodbg=1\b/.test(location.href);

/**
 * EXPERIMENTS for the iPhone's music crackling while its mic is captured
 * (docs/LOCAL_DEV.md, "Tracing the sound engine on a real device"): `audiofix=`
 * takes a comma list. Only `noaec` is left - `rebuild` and `playback` did not
 * help on the phone, and `release` is now simply how muting works. Read like
 * audiodbg, off the whole href. Absent, nothing changes.
 */
export const audioFixes: readonly string[] =
  typeof location !== "undefined" ? (/[?&]audiofix=([\w,]+)/.exec(location.href)?.[1]?.split(",").filter(Boolean) ?? []) : [];

export function audioFix(name: "noaec"): boolean {
  return audioFixes.includes(name);
}

const MAX_LINES = 14;
const lines: string[] = [];
let panel: HTMLElement | null = null;
let state: () => string = () => "";
/** Lines other modules add under the engine's own (voice chat's context and mic). */
const extras = new Map<string, () => string>();

/** Records an event: shown newest first with seconds since page load. */
export function audioLog(message: string): void {
  if (!audioDiagEnabled) return;
  lines.unshift(`${(performance.now() / 1000).toFixed(1).padStart(6)}s  ${message}`);
  lines.length = Math.min(lines.length, MAX_LINES);
  render();
}

/** What the panel's first line reports, re-read every half second. */
export function audioDiagState(read: () => string): void {
  if (!audioDiagEnabled) return;
  state = read;
  ensurePanel();
}

/** Another state line, under the engine's, keyed so a re-registration replaces it. */
export function audioDiagLine(key: string, read: () => string): void {
  if (!audioDiagEnabled) return;
  extras.set(key, read);
  ensurePanel();
}

function ensurePanel(): void {
  if (panel) return;
  panel = document.createElement("pre");
  panel.setAttribute("aria-hidden", "true");
  Object.assign(panel.style, {
    position: "fixed",
    left: "4px",
    bottom: "4px",
    zIndex: "2147483647",
    margin: "0",
    padding: "6px 8px",
    maxWidth: "calc(100vw - 8px)",
    font: "10px/1.35 ui-monospace, Menlo, Consolas, monospace",
    color: "#e6ffe6",
    background: "rgba(0, 0, 0, 0.78)",
    borderRadius: "6px",
    pointerEvents: "none",
    whiteSpace: "pre-wrap",
  } satisfies Partial<CSSStyleDeclaration>);
  const mount = () => document.body.appendChild(panel!);
  if (document.body) mount();
  else window.addEventListener("DOMContentLoaded", mount, { once: true });
  window.setInterval(render, 500);
}

function render(): void {
  if (!panel) return;
  const extra = [...extras.values()].map((read) => read());
  panel.textContent = [state(), ...extra, ...lines].join("\n");
}
